import Vue from "vue";
import moment from "moment";
import { detectUrl, formatPhone, randomId } from "@/utils";
import {
  CallState,
  CallFilter,
  CallDateFilter,
  CallLogEntry,
  Tab,
  Flow,
  Reason,
  Status,
  Tabs,
  User,
  CallUserFilter,
} from "./types";
import { CallLogType, DateTimeSpan, TemplateMessageType } from "./enums";
import { StaticWebsite } from "@azure/storage-blob";

const getDefaultFilter = (): CallFilter => {
  return {
    id: "",
    phoneId: "",
    callerPhone: "",
    reasonList: [],
    ownerUserList: [],
    statusList: [],
    created: {
      timeSpan: DateTimeSpan.LAST7,
      dateFrom: null,
      dateTo: null,
    },
    updated: {
      timeSpan: null,
      dateFrom: null,
      dateTo: null,
    },
    orderBy: "CREATED_DESC",
  };
};

const dashboardTab: Tab = {
  id: "DASHBOARD",
  name: "Inbox",
  component: "Dashboard",
  props: {
    tabId: "DASHB",
  },
  showRemove: false,
};

const configTab: Tab = {
  id: "CONFIG",
  name: "Configuration",
  component: "Config",
  props: {
    tabId: "CONFIG",
  },
  restrict: {
    setting: "ENABLE_CALLS_MODULE_CONFIG",
  },
  showRemove: false,
};

const getDefaultTabs = (): Tabs => {
  return {
    active: dashboardTab,
    list: [dashboardTab],
    // list: [dashboardTab, configTab],
  };
};

const getDefaultState = (): CallState => {
  return {
    tabs: getDefaultTabs(),
    dashboard: {
      filter: getDefaultFilter(),
      userFilters: {
        list: [],
        selected: null,
      },
      dates: [],
      chats: [],
    },
    langs: [
      {
        id: "en",
        name: "English",
      },
      {
        id: "es",
        name: "Spanish",
      },
    ],
    phones: [],
    reasons: [],
    users: [],
    status: [],
    recordings: [],
    flows: [],
    notif: {
      calls: true,
      chats: true,
    },
  };
};

const getDateFilterRange = (filter: CallDateFilter) => {
  let from: any = null;
  let to: any = null;
  if (filter.timeSpan) {
    switch (filter.timeSpan) {
      case DateTimeSpan.TODAY:
        from = moment().format("YYYY-MM-DD");
        break;
      case DateTimeSpan.WEEK:
        from = moment()
          .startOf("week")
          .format("YYYY-MM-DD");
        break;
      case DateTimeSpan.LAST7:
        from = moment()
          .subtract(7, "day")
          .format("YYYY-MM-DD");
        break;
      case DateTimeSpan.MONTH:
        from = moment()
          .startOf("month")
          .format("YYYY-MM-DD");
        break;
    }
    to = moment().format("YYYY-MM-DD");
  } else {
    from = moment(filter.dateFrom).format("YYYY-MM-DD");
    to = moment(filter.dateTo).format("YYYY-MM-DD");
  }
  return { from, to };
};

export default {
  namespaced: true,
  state: getDefaultState(),
  mutations: {
    // TABS
    SET_ACTIVE_TAB(state: CallState, tab: Tab) {
      state.tabs.active = tab;
    },
    ADD_NEW_TAB(state: CallState, tab: Tab) {
      state.tabs.list.push(tab);
      state.tabs.active = tab;
    },
    SET_TAB_NAME(state: CallState, { id, name }) {
      const tab = state.tabs.list.find((t) => t.id === id);
      if (tab) tab.name = name;
    },
    SET_TAB_FLOW_RECORD(state: CallState, { tabId, record }) {
      const tab = state.tabs.list.find((t) => t.id === tabId);
      if (tab) tab.props.flowRecord = record;
    },
    SET_TAB_STATUS_RECORD(state: CallState, { tabId, record }) {
      const tab = state.tabs.list.find((t) => t.id === tabId);
      if (tab) tab.props.statusRecord = record;
    },
    REMOVE_TAB(state: CallState, id: string) {
      const { list } = state.tabs;
      const index = list.findIndex((t) => t.id === id);
      list.splice(index, 1);
      if (id.includes("FLOW_")) {
        state.tabs.active = configTab;
      } else {
        state.tabs.active = dashboardTab;
      }
    },
    RESET_TABS(state: CallState) {
      state.tabs = getDefaultTabs();
    },
    // DATES / CALLS
    SET_CALL_DATES(state: CallState, dates: any[]) {
      state.dashboard.dates = dates.map((date, index) => {
        date.date = moment(date.date).format("YYYY-MM-DD");
        date.calls = {
          list: [],
          total: Object.keys(date).reduce((count, key) => {
            if (!["date", "visible"].includes(key)) {
              count += date[key] || 0;
            }
            return count;
          }, 0),
        };
        date.visible = index === 0 && date.calls.total;
        return date;
      });
    },
    SET_DATE_CALLS(state: CallState, { date, calls }) {
      const _date = state.dashboard.dates.find((item) => item.date === date);
      if (date) {
        _date.calls.list = calls.map((item) => {
          item.checked = false;
          return item;
        });
        _date.calls.total = calls.length;
      }
    },
    CHECK_UNCHECK_CALL(state: CallState, call: any) {
      call.checked = !call.checked;
    },
    UNCHECK_CALLS(state: CallState) {
      const { dates } = state.dashboard;
      dates.forEach((date) => {
        date.calls.list
          .filter((call: any) => call.checked)
          .forEach((call: any) => (call.checked = false));
      });
    },
    // CHATS
    ADD_NEW_CHAT(state: CallState, { call, unread, collapsed }) {
      const { chats } = state.dashboard;
      const index = chats.findIndex((item) => item.call.id === call.id);
      if (index > -1) {
        const info = chats.splice(index, 1)[0];
        info.unread = unread;
        if (unread && info.collapsed) {
          info.collapsed = collapsed;
        }
        chats.unshift(info);
      } else {
        chats.unshift({
          key: randomId(6),
          call: JSON.parse(JSON.stringify(call)),
          unread,
          collapsed,
        });
      }
    },
    COLLAPSE_EXPAND_CHAT(state: CallState, key: string) {
      const { chats } = state.dashboard;
      const info = chats.find((item) => item.key === key);
      if (info) {
        info.collapsed = !info.collapsed;
        info.unread = false;
      }
    },
    OPEN_CHAT(state: CallState, key: string) {
      const { chats } = state.dashboard;
      const index = chats.findIndex((item) => item.key === key);
      const info = chats.splice(index, 1)[0];
      info.collapsed = false;
      chats.unshift(info);
    },
    REMOVE_CHAT(state: CallState, key: string) {
      const { chats } = state.dashboard;
      const index = chats.findIndex((item) => item.key === key);
      chats.splice(index, 1);
    },
    // NOTIFICATIONS
    SET_CALLS_NOTIF_ON_OFF(state: CallState) {
      state.notif.calls = !state.notif.calls;
    },
    // FILTER
    SET_FILTER(state: CallState, filter: CallFilter) {
      state.dashboard.filter = filter;
    },
    SET_PHONE_FILTER(state: CallState, id: string) {
      state.dashboard.filter.phoneId = id;
    },
    SET_ID_FILTER(state: CallState, id: string) {
      state.dashboard.filter.id = id;
    },
    SET_CALLER_PHONE_FILTER(state: CallState, phone: string) {
      state.dashboard.filter.callerPhone = phone;
    },
    SET_REASON_FILTER(state: CallState, filter: string[]) {
      state.dashboard.filter.reasonList = filter;
    },
    SET_OWNER_FILTER(state: CallState, filter: string[]) {
      state.dashboard.filter.ownerUserList = filter;
    },
    SET_STATUS_FILTER(state: CallState, filter: string[]) {
      state.dashboard.filter.statusList = filter;
    },
    SET_CREATED_FILTER(state: CallState, filter: CallDateFilter) {
      state.dashboard.filter.created = filter;
    },
    SET_UPDATED_FILTER(state: CallState, filter: CallDateFilter) {
      state.dashboard.filter.updated = filter;
    },
    SET_ORDER_BY_FILTER(state: CallState, field: string) {
      state.dashboard.filter.orderBy = field;
    },
    RESET_FILTER(state: CallState) {
      state.dashboard.filter = getDefaultFilter();
      state.dashboard.userFilters.selected =
        state.dashboard.userFilters.list[0];
    },
    SET_USER_FILTERS(state: CallState, filters: CallUserFilter[]) {
      state.dashboard.userFilters.list = filters;
      state.dashboard.userFilters.selected = filters[0];
    },
    SET_SELECTED_USER_FILTER(state: CallState, filter: CallUserFilter) {
      state.dashboard.userFilters.selected = filter;
      localStorage.setItem("LAST_USER_FILTER", filter.id.toString());
    },
    SELECT_LAST_USER_FILTER(state: CallState) {
      const id = localStorage.getItem("LAST_USER_FILTER") || -1;
      const filter = state.dashboard.userFilters.list.find(
        (item) => item.id == id
      );
      if (filter) {
        state.dashboard.userFilters.selected = filter;
        state.dashboard.filter = <CallFilter>JSON.parse(filter.json);
      }
    },
    // CONFIGURATION
    SET_PHONES(state: CallState, phones: Array<any>) {
      state.phones = phones;
    },
    SET_REASONS(state: CallState, reasons: Array<any>) {
      state.reasons = reasons;
    },
    SET_REASON_POSITIONS(state: CallState, reasons: Array<any>) {
      state.reasons = reasons;
    },
    SET_USERS(state: CallState, users: Array<any>) {
      state.users = users;
    },
    SET_STATUS(state: CallState, status: Array<any>) {
      state.status = status.map((item: any) => {
        item.icon = item.hasActions ? "fas fa-bolt" : null;
        item.config = JSON.parse(item.config);
        return item;
      });
    },
    SET_STATUS_POSITIONS(state: CallState, status: Array<any>) {
      state.status = status;
    },
    SET_RECORDINGS(state: CallState, recordings: Array<any>) {
      state.recordings = recordings;
    },
    SET_FLOWS(state: CallState, flows: Array<any>) {
      state.flows = flows.map((item) => {
        if (item.modes) {
          item.modes = JSON.parse(item.modes);
        } else {
          item.modes = {
            current: "MAIN",
            list: [
              {
                key: "MAIN",
                name: "Main",
                backColor: "#004598",
                fontColor: "#FFFFFF",
              },
            ],
          };
        }
        return item;
      });
    },
    // RESET
    RESET_STATE(state: CallState) {
      Object.assign(state, getDefaultState());
    },
  },
  actions: {
    // CALLS
    async getTodayCalls({ rootState, state, commit, dispatch }) {
      const today = moment().format("YYYY-MM-DD");
      // IF TODAY IS NOT IN DATES, GET ALL
      const dates = state.dashboard.dates as any[];
      if (dates.findIndex((item) => item.date === today) === -1) {
        dispatch("getDates");
        return;
      }
      // SET FORM DATA
      const filter = state.dashboard.filter as CallFilter;
      const params = new FormData();
      params.append("entityId", rootState.auth.entityId);
      params.append("phoneId", filter.phoneId);
      params.append("id", filter.id);
      params.append("callerPhone", filter.callerPhone);
      if (filter.reasonList && filter.reasonList.length) {
        params.append(
          "reasonList",
          filter.reasonList
            .map((reason) => {
              return "'" + reason + "'";
            })
            .join()
        );
      }
      if (filter.ownerUserList && filter.ownerUserList.length) {
        params.append(
          "ownerUserList",
          filter.ownerUserList
            .map((owner) => {
              return "'" + owner + "'";
            })
            .join()
        );
      }
      if (filter.statusList && filter.statusList.length) {
        params.append(
          "statusList",
          filter.statusList
            .map((status) => {
              return "'" + status + "'";
            })
            .join()
        );
      }
      params.append("recordDateFrom", today);
      params.append("orderBy", filter.orderBy);
      // GET DATE CALLS ASYNC
      const { data } = await Vue.axios.post(
        "callcenter/call/date/calls",
        params
      );
      //
      commit("SET_DATE_CALLS", { date: today, calls: data });
    },
    async getDates({ rootState, state, commit, dispatch }) {
      // SET FORM DATA
      const filter = state.dashboard.filter as CallFilter;
      const params = new FormData();
      params.append("entityId", rootState.auth.entityId);
      params.append("phoneId", filter.phoneId);
      params.append("id", filter.id);
      params.append("callerPhone", filter.callerPhone);
      if (filter.reasonList && filter.reasonList.length) {
        params.append(
          "reasonList",
          filter.reasonList
            .map((reason) => {
              return "'" + reason + "'";
            })
            .join()
        );
      }
      if (filter.ownerUserList && filter.ownerUserList.length) {
        params.append(
          "ownerUserList",
          filter.ownerUserList
            .map((owner) => {
              return "'" + owner + "'";
            })
            .join()
        );
      }
      if (filter.statusList && filter.statusList.length) {
        params.append(
          "statusList",
          filter.statusList
            .map((status) => {
              return "'" + status + "'";
            })
            .join()
        );
      }
      // GET CREATED FILTER
      const created = getDateFilterRange(filter.created);
      params.append("recordDateFrom", created.from);
      params.append("recordDateTo", created.to);
      // GET UPDATED FILTER
      const updated = getDateFilterRange(filter.updated);
      params.append("lastUpdateFrom", updated.from);
      params.append("lastUpdateTo", updated.to);
      //
      params.append("orderBy", filter.orderBy);
      // GET CALL DATES ASYNC
      const { data } = await Vue.axios.post(
        "callcenter/call/date/list",
        params
      );
      // SET CALL DATES ARRAY
      commit("SET_CALL_DATES", data);
      // GET CALLS FOR FIRST DATE
      if (data.length) {
        dispatch("getCalls", data[0].date);
      }
    },
    async getCalls({ rootState, state, commit }, date: string) {
      // SET FORM DATA
      const filter = state.dashboard.filter as CallFilter;
      const params = new FormData();
      params.append("entityId", rootState.auth.entityId);
      params.append("phoneId", filter.phoneId);
      params.append("id", filter.id);
      params.append("callerPhone", filter.callerPhone);
      if (filter.reasonList && filter.reasonList.length) {
        params.append(
          "reasonList",
          filter.reasonList
            .map((reason) => {
              return "'" + reason + "'";
            })
            .join()
        );
      }
      if (filter.ownerUserList && filter.ownerUserList.length) {
        params.append(
          "ownerUserList",
          filter.ownerUserList
            .map((owner) => {
              return "'" + owner + "'";
            })
            .join()
        );
      }
      if (filter.statusList && filter.statusList.length) {
        params.append(
          "statusList",
          filter.statusList
            .map((status) => {
              return "'" + status + "'";
            })
            .join()
        );
      }
      params.append("recordDateFrom", moment(date).format("YYYY-MM-DD"));
      params.append("orderBy", filter.orderBy);
      // GET DATE CALLS ASYNC
      const { data } = await Vue.axios.post(
        "callcenter/call/date/calls",
        params
      );
      //
      commit("SET_DATE_CALLS", { date, calls: data });
    },
    async getCallsCSV({ rootState, state }) {
      // SET FORM DATA
      const filter = state.dashboard.filter as CallFilter;
      const params = new FormData();
      params.append("entityId", rootState.auth.entityId);
      params.append("phoneId", filter.phoneId);
      params.append("id", filter.id);
      params.append("callerPhone", filter.callerPhone);
      if (filter.reasonList && filter.reasonList.length) {
        params.append(
          "reasonList",
          filter.reasonList
            .map((reason) => {
              return "'" + reason + "'";
            })
            .join()
        );
      }
      if (filter.ownerUserList && filter.ownerUserList.length) {
        params.append(
          "ownerUserList",
          filter.ownerUserList
            .map((owner) => {
              return "'" + owner + "'";
            })
            .join()
        );
      }
      if (filter.statusList && filter.statusList.length) {
        params.append(
          "statusList",
          filter.statusList
            .map((status) => {
              return "'" + status + "'";
            })
            .join()
        );
      }
      // GET CREATED FILTER
      const created = getDateFilterRange(filter.created);
      params.append("recordDateFrom", created.from);
      params.append("recordDateTo", created.to);
      // GET UPDATED FILTER
      const updated = getDateFilterRange(filter.updated);
      params.append("lastUpdateFrom", updated.from);
      params.append("lastUpdateTo", updated.to);
      //
      params.append("orderBy", filter.orderBy);
      // GET CALL LIST CSV ASYNC
      const { data } = await Vue.axios.post(
        "callcenter/call/list/csv",
        params,
        {
          responseType: "blob",
        }
      );
      //
      return data;
    },
    async getCallsExcel({ rootState, state }) {
      // SET FORM DATA
      const filter = state.dashboard.filter as CallFilter;
      const params = new FormData();
      params.append("entityId", rootState.auth.entityId);
      params.append("phoneId", filter.phoneId);
      params.append("id", filter.id);
      params.append("callerPhone", filter.callerPhone);
      if (filter.reasonList && filter.reasonList.length) {
        params.append(
          "reasonList",
          filter.reasonList
            .map((reason) => {
              return "'" + reason + "'";
            })
            .join()
        );
      }
      if (filter.ownerUserList && filter.ownerUserList.length) {
        params.append(
          "ownerUserList",
          filter.ownerUserList
            .map((owner) => {
              return "'" + owner + "'";
            })
            .join()
        );
      }
      if (filter.statusList && filter.statusList.length) {
        params.append(
          "statusList",
          filter.statusList
            .map((status) => {
              return "'" + status + "'";
            })
            .join()
        );
      }
      // GET CREATED FILTER
      const created = getDateFilterRange(filter.created);
      params.append("recordDateFrom", created.from);
      params.append("recordDateTo", created.to);
      // GET UPDATED FILTER
      const updated = getDateFilterRange(filter.updated);
      params.append("lastUpdateFrom", updated.from);
      params.append("lastUpdateTo", updated.to);
      //
      params.append("orderBy", filter.orderBy);
      // GET CALL LIST EXCEL ASYNC
      const { data } = await Vue.axios.post(
        "callcenter/call/list/excel",
        params,
        {
          responseType: "blob",
        }
      );
      //
      return data;
    },
    async getVoicemailData({ commit }, id: string) {
      // SET FORM DATA
      const params = new FormData();
      params.append("id", id);
      // GET VOICEMAIL DATA ASYNC
      const { data } = await Vue.axios.post(
        "callcenter/call/voicemail/data",
        params,
        {
          responseType: "blob",
        }
      );
      //
      return data;
    },
    async saveCallLang({ state }, { callId, lang }) {
      // SET FORM DATA
      const params = new FormData();
      params.append("callId", callId);
      params.append("lang", lang);
      // SAVE OWNER ASYNC
      const { data } = await Vue.axios.post("callcenter/call/lang", params);
      //
      return data;
    },
    async saveCallReason({ state }, { callId, reason }) {
      // SET FORM DATA
      const params = new FormData();
      params.append("callId", callId);
      params.append("reason", reason);
      // SAVE REASON ASYNC
      const { data } = await Vue.axios.post("callcenter/call/reason", params);
      //
      return data;
    },
    async saveCallOwner({ state }, { callId, ownerUser }) {
      // SET FORM DATA
      const params = new FormData();
      params.append("callId", callId);
      params.append("ownerUser", ownerUser);
      // SAVE OWNER ASYNC
      const { data } = await Vue.axios.post("callcenter/call/owner", params);
      //
      return data;
    },
    async saveCallStatus({ state, getters }, { callId, status }) {
      // SET FORM DATA
      const params = new FormData();
      params.append("callId", callId);
      params.append("status", status);
      // SAVE STATUS ASYNC
      const { data } = await Vue.axios.post("callcenter/call/status", params);
      //
      return data;
    },
    async saveCheckedCallStatus({ commit, getters }, status: any) {
      // SET FORM DATA
      const params = new FormData();
      const list = getters.checkedCalls.map((item: any) => item.id);
      params.append("list", list);
      params.append("status", status.name);
      // SAVE STATUS ASYNC
      const { data } = await Vue.axios.post("callcenter/call/status", params);
      // UPDATE LOCAL STATUS
      getters.checkedCalls.forEach((item: any) => {
        item.status = status.name;
        item.statusBG = status.backColor || "white";
        item.statusFC = status.fontColor || "black";
      });
      // UNCHECK CALLS
      commit("UNCHECK_CALLS");
      //
      return data;
    },
    async saveManualCall({ rootState, dispatch }, call: any) {
      // SET FORM DATA
      const params = new FormData();
      params.append("entityId", rootState.auth.entityId);
      params.append("fromPhone", call.fromPhone);
      params.append("toPhone", call.toPhone);
      params.append("lang", call.lang || "");
      params.append("reason", call.reason);
      params.append("owner", call.owner);
      params.append("status", call.status);
      params.append("message", call.chat.message);
      params.append("fileName", call.chat.file.name);
      params.append("fileSize", call.chat.file.size);
      params.append("fileContentType", call.chat.file.contentType);
      params.append("fileStorageId", call.chat.file.storageId);
      // SAVE FILTER ASYNC
      const { data } = await Vue.axios.post("callcenter/call/manual", params);
      // RELOAD TODAY'S CALLS
      dispatch("getTodayCalls");
      // RETURN NEWLY CREATED CALL
      return data;
      //
    },
    // LOG
    async getLog({ commit }, { id, typeList }) {
      // SET FORM DATA
      const params = new FormData();
      params.append("callId", id);
      if (typeList) params.append("typeList", typeList);
      // GET CALL LOG ASYNC
      const { data } = await Vue.axios.post("callcenter/call/log/list", params);
      //
      return data;
    },
    async getChat({ commit }, id: string) {
      // SET FORM DATA
      const params = new FormData();
      params.append("callId", id);
      // GET CHAT LOG ASYNC
      let { data } = await Vue.axios.post("callcenter/call/log/chat", params);
      data = data.map((m: any) => {
        if (m.type === CallLogType.SECURE_CHAT_RECORDING) {
          m.recordingStorageId = m.message.split("*")[1];
          m.message = m.message.split("*")[0];
        } else if (m.type === CallLogType.SECURE_CHAT_TTS) {
          m.voice = m.message.split("*")[1];
          m.message = m.message.split("*")[0];
        } else {
          // DETECT URLs AND CONVERT THEM HYPERLINKS
          // m.message = detectUrl(m.message);
        }
        return m;
      });
      //
      return data;
    },
    async saveLog({ commit }, entry: CallLogEntry) {
      // SET FORM DATA
      const params = new FormData();
      params.append("id", entry.id ? entry.id : "");
      params.append("callId", entry.callId);
      params.append("typeId", entry.typeId);
      params.append("comment", entry.comment ? entry.comment : "");
      // SAVE CALL LOG ASYNC
      const { data } = await Vue.axios.post("callcenter/call/log/save", params);
      //
      return data;
    },
    // FLOWS
    async getFlows({ rootState, commit }) {
      // SET FORM DATA
      const params = new FormData();
      params.append("entityId", rootState.auth.entityId);
      // GET FLOWS ASYNC
      const { data } = await Vue.axios.post("callcenter/flow/list", params);
      //
      commit("SET_FLOWS", data);
    },
    async getFlowRecord({ commit }, id: string) {
      // SET FORM DATA
      const params = new FormData();
      params.append("id", id);
      // GET FLOW RECORD ASYNC
      const { data } = await Vue.axios.post("callcenter/flow/record", params);
      //
      return data;
    },
    async saveFlow({ rootState, dispatch }, flow: Flow): Promise<string> {
      // SET FORM DATA
      const params = new FormData();
      if (flow.id) params.append("id", flow.id);
      params.append("entityId", rootState.auth.entityId);
      params.append("name", flow.name);
      params.append("description", flow.description);
      params.append("config", JSON.stringify(flow));
      // SAVE FLOW ASYNC
      const { data } = await Vue.axios.post("callcenter/flow/save", params);
      // RELOAD FLOWS
      dispatch("getFlows");
      // RETURN FLOW ID
      return data.id;
      //
    },
    async cloneFlow({ dispatch }, { entityId, id, name }) {
      // SET FORM DATA
      const params = new FormData();
      params.append("entityId", entityId);
      params.append("id", id);
      params.append("name", name);
      // CLONE FLOW ASYNC
      await Vue.axios.post("callcenter/flow/clone", params);
      // RELOAD FLOWS
      dispatch("getFlows");
      //
    },
    async saveFlowPhone({ dispatch }, { phoneId, flowId }) {
      // SET FORM DATA
      const params = new FormData();
      params.append("phoneId", phoneId);
      params.append("flowId", flowId);
      // SAVE FLOW PHONE ASYNC
      await Vue.axios.post("callcenter/flow/phone/save", params);
      // RELOAD PHONES
      dispatch("getPhones");
      //
    },
    async removeFlowPhone({ dispatch }, { phoneId, flowId }) {
      // SET FORM DATA
      const params = new FormData();
      params.append("phoneId", phoneId);
      params.append("flowId", flowId);
      // REMOVE FLOW PHONE ASYNC
      await Vue.axios.post("callcenter/flow/phone/remove", params);
      // RELOAD PHONES
      dispatch("getPhones");
      //
    },
    async archiveFlow({ dispatch }, id: string) {
      // SET FORM DATA
      const params = new FormData();
      params.append("id", id);
      // ARCHIVE PHONE ASYNC
      await Vue.axios.post("callcenter/flow/archive", params);
      // RELOAD FLOWS
      dispatch("getFlows");
      //
    },
    async setFlowMode({ dispatch }, { flowId, modeKey }) {
      // SET FORM DATA
      const params = new FormData();
      params.append("flowId", flowId);
      params.append("modeKey", modeKey);
      // SET FLOW MODE
      await Vue.axios.post("callcenter/flow/mode", params);
      // RELOAD FLOWS
      await dispatch("getFlows");
      //
    },
    // PHONES
    async getPhones({ rootState, commit }) {
      // SET FORM DATA
      const params = new FormData();
      params.append("entityId", rootState.auth.entityId);
      // GET PHONES ASYNC
      const { data } = await Vue.axios.post("callcenter/phone/list", params);
      //
      const phones = data.map((item) => {
        item.number = formatPhone(item.number);
        return item;
      });
      commit("SET_PHONES", phones);
    },
    async getAvailablePhones({ commit }, { country, areaCode }) {
      // SET FORM DATA
      const params = new FormData();
      params.append("country", country);
      if (country === "US" && areaCode) params.append("areaCode", areaCode);
      // GET AVAILABLE PHONES ASYNC
      const { data } = await Vue.axios.post(
        "callcenter/phone/list/available",
        params
      );
      //
      return data;
    },
    async savePhone({ rootState, dispatch }, number: string) {
      // SET FORM DATA
      const params = new FormData();
      params.append("entityId", rootState.auth.entityId);
      params.append("number", number);
      // SAVE PHONE ASYNC
      await Vue.axios.post("callcenter/phone/save", params);
      // RELOAD PHONES
      dispatch("getPhones");
      //
    },
    async savePhoneName({ commit }, { id, name }) {
      // SET FORM DATA
      const params = new FormData();
      params.append("id", id);
      params.append("name", name);
      // SAVE PHONE NAME ASYNC
      const { data } = await Vue.axios.post(
        "callcenter/phone/save/name",
        params
      );
      //
      return data;
    },
    async archivePhone({ dispatch }, id: string) {
      // SET FORM DATA
      const params = new FormData();
      params.append("id", id);
      // ARCHIVE PHONE ASYNC
      await Vue.axios.post("callcenter/phone/archive", params);
      // RELOAD PHONES
      dispatch("getPhones");
      //
    },
    async sendSMS({ commit }, { callId, text }) {
      // SET FORM DATA
      const params = new FormData();
      params.append("callId", callId);
      params.append("text", text);
      // SEND SMS ASYNC
      const { data } = await Vue.axios.post("callcenter/call/sms", params);
      //
      return data;
    },
    // REASONS
    async getReasons({ rootState, commit }) {
      // SET FORM DATA
      const params = new FormData();
      params.append("entityId", rootState.auth.entityId);
      // GET REASONS ASYNC
      const { data } = await Vue.axios.post("callcenter/reason/list", params);
      //
      commit("SET_REASONS", data);
    },
    async saveReason({ rootState, dispatch }, reason: Reason) {
      // SET FORM DATA
      const params = new FormData();
      if (reason.id) params.append("id", reason.id);
      params.append("entityId", rootState.auth.entityId);
      params.append("name", reason.name);
      params.append("fontColor", reason.fontColor);
      params.append("backColor", reason.backColor);
      // SAVE REASON ASYNC
      await Vue.axios.post("callcenter/reason/save", params);
      // RELOAD REASONS
      dispatch("getReasons");
      //
    },
    async archiveReason({ dispatch }, id: string) {
      // SET FORM DATA
      const params = new FormData();
      params.append("id", id);
      // ARCHIVE REASON ASYNC
      await Vue.axios.post("callcenter/reason/archive", params);
      // RELOAD REASON
      dispatch("getReasons");
      //
    },
    async setReasonPositions({ rootState }, list: any[]) {
      // SET FORM DATA
      const params = new FormData();
      params.append("entityId", rootState.auth.entityId);
      params.append("json", JSON.stringify(list));
      // SAVE REASON POSITIONS ASYNC
      await Vue.axios.post("callcenter/reason/positions", params);
      //
    },
    // STATUS
    async getStatus({ rootState, commit }) {
      // SET FORM DATA
      const params = new FormData();
      params.append("entityId", rootState.auth.entityId);
      // GET STATUS ASYNC
      const { data } = await Vue.axios.post("callcenter/status/list", params);
      //
      commit("SET_STATUS", data);
    },
    async saveStatus({ rootState, dispatch }, status: Status): Promise<string> {
      // SET FORM DATA
      const params = new FormData();
      if (status.id) params.append("id", status.id);
      params.append("entityId", rootState.auth.entityId);
      params.append("name", status.name);
      params.append("fontColor", status.fontColor);
      params.append("backColor", status.backColor);
      params.append("config", JSON.stringify(status.config));
      // SAVE STATUS ASYNC
      const { data } = await Vue.axios.post("callcenter/status/save", params);
      // RELOAD STATUS
      dispatch("getStatus");
      // RETURN STATUS ID
      return data.id;
      //
    },
    async archiveStatus({ dispatch }, id: string) {
      // SET FORM DATA
      const params = new FormData();
      params.append("id", id);
      // ARCHIVE STATUS ASYNC
      await Vue.axios.post("callcenter/status/archive", params);
      // RELOAD STATUS
      dispatch("getStatus");
      //
    },
    async setStatusPositions({ rootState }, list: any[]) {
      // SET FORM DATA
      const params = new FormData();
      params.append("entityId", rootState.auth.entityId);
      params.append("json", JSON.stringify(list));
      // SAVE STATUS POSITIONS ASYNC
      await Vue.axios.post("callcenter/status/positions", params);
      //
    },
    async getStatusSummary({ rootState, commit }) {
      // SET FORM DATA
      const params = new FormData();
      params.append("entityId", rootState.auth.entityId);
      // GET SUMMARY ASYNC
      const { data } = await Vue.axios.post(
        "callcenter/status/summary",
        params
      );
      //
      return data;
    },
    // RECORDINGS
    async getRecordings({ rootState, commit }) {
      // SET FORM DATA
      const params = new FormData();
      params.append("entityId", rootState.auth.entityId);
      // GET RECORDINGS ASYNC
      const { data } = await Vue.axios.post(
        "callcenter/recording/list",
        params
      );
      //
      commit("SET_RECORDINGS", data);
    },
    async archiveRecording({ dispatch }, id: string) {
      // SET FORM DATA
      const params = new FormData();
      params.append("id", id);
      // ARCHIVE RECORDING ASYNC
      await Vue.axios.post("callcenter/recording/archive", params);
      // RELOAD RECORDINGS
      dispatch("getRecordings");
      //
    },
    // USERS
    async getUsers({ rootState, commit }) {
      // SET FORM DATA
      const params = new FormData();
      params.append("entityId", rootState.auth.entityId);
      // GET USERS ASYNC
      const { data } = await Vue.axios.post("callcenter/user/list", params);
      //
      commit("SET_USERS", data);
    },
    async getFilters({ rootState, commit, dispatch }, selectLast: boolean) {
      const user: User = rootState.auth.user;
      // SET OWNER FILTER
      const filter = getDefaultFilter();
      filter.ownerUserList = [user.userName];
      //
      const filters = [
        {
          id: -1,
          name: "No Filter",
          json: JSON.stringify(getDefaultFilter()),
          userId: user.id,
        },
        {
          id: 0,
          name: "My Calls",
          json: JSON.stringify(filter),
          userId: user.id,
        },
      ];
      // SET FORM DATA
      const params = new FormData();
      params.append("entityId", rootState.auth.entityId);
      // GET USER FILTERS ASYNC
      const { data } = await Vue.axios.post(
        "callcenter/user/filter/list",
        params
      );
      filters.push(...data);
      //
      commit("SET_USER_FILTERS", filters);
      // SELECT LAST USER (IF EXISTS)
      if (selectLast) {
        commit("SELECT_LAST_USER_FILTER");
        dispatch("getDates");
      }
    },
    async saveFilter({ rootState, state }, { name, isPublic }) {
      // SET FORM DATA
      const params = new FormData();
      params.append("entityId", rootState.auth.entityId);
      params.append("name", name);
      params.append("json", JSON.stringify(state.dashboard.filter));
      params.append("public", isPublic);
      // SAVE FILTER ASYNC
      const { data } = await Vue.axios.post(
        "callcenter/user/filter/save",
        params
      );
      //
      return data;
    },
    async archiveFilter(context, filterId: number) {
      // SET FORM PARAMS
      const params = new FormData();
      params.append("filterId", String(filterId));
      // ARCHIVE FILTER ASYNC
      const { data } = await Vue.axios.post(
        "callcenter/user/filter/archive",
        params
      );
      //
      return data;
    },
    // DICTATION
    async getDictationToken() {
      // GET TOKEN ASYNC
      const { data } = await Vue.axios.post(
        "callcenter/call/chat/dictation/token"
      );
      //
      return data;
    },
    // TEMPLATES
    async getChatTemplateMessages({ rootState }, id: string) {
      // SET FORM DATA
      const params = new FormData();
      params.append("callId", id);
      // GET TEMPLATE MESSAGES ASYNC
      let { data } = await Vue.axios.post(
        "callcenter/template/messages/chat",
        params
      );
      data = data.map((item: any) => {
        item.messages = item.messages.map((message: any) => {
          if (message.type === TemplateMessageType.TEXT) {
            message.text = message.text
              .replace(
                "@USER_NAME",
                `<span class="wm-fw-500">${rootState.auth.user.userName}</span>`
              )
              .replace(
                "@USER_LONG_NAME",
                `<span class="wm-fw-500">${rootState.auth.user.userLongName}</span>`
              );
          }
          return message;
        });
        return item;
      });
      //
      return data;
    },
    async sendChat(context, { id, list, notify }) {
      // SET FORM DATA
      const params = new FormData();
      params.append("callId", id);
      params.append("list", JSON.stringify(list));
      params.append("notify", notify);
      // SEND CHAT ASYNC
      await Vue.axios.post("callcenter/call/chat/send", params);
      //
    },
  },
  getters: {
    checkedCalls: (state: CallState) => {
      return state.dashboard.dates.reduce((calls: Array<any>, date: any) => {
        const _calls = date.calls.list.filter((item: any) => item.checked);
        return calls.concat(_calls);
      }, []);
    },
    checkedCallCount: (state: CallState) => {
      return state.dashboard.dates.reduce((count, date) => {
        count += date.calls.list.filter((item: any) => item.checked).length;
        return count;
      }, 0);
    },
  },
};
